import { useEffect, useRef } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { Button, ButtonLink, IconButton, Image, imageAssets, useToast } from '@yingbaba/ui';
import {
  ArrowRight,
  ArrowUpRight,
  BookOpen,
  BriefcaseBusiness,
  Camera,
  CheckCircle2,
  ChevronDown,
  Copy,
  FileText,
  Film,
  FolderOpen,
  Mail,
  Megaphone,
  MessageSquare,
  Search,
  ShieldCheck,
  Sparkles,
  Users,
  X,
} from '@yingbaba/icons';
import { siteURL } from '@yingbaba/utils';
import { helpCategories, searchHelp } from './helpContent';
import { openSupport } from './SupportWidget';

const operator = '广州和晟网络科技有限公司';
const contactEmail = 'company@yingbaba.com';
const registration = (role: 'client' | 'provider' | 'resource') =>
  `/login?mode=register&role=${role}&returnTo=${encodeURIComponent(role === 'provider' ? '/provider' : role === 'resource' ? '/resources' : '/')}`;

function Breadcrumb({ title }: { title: string }) {
  return (
    <nav className="information-breadcrumb" aria-label="当前位置">
      <Link to="/">首页</Link>
      <span aria-hidden="true">/</span>
      <span aria-current="page">{title}</span>
    </nav>
  );
}

function ContactSection() {
  const toast = useToast();
  async function copyEmail() {
    try {
      await navigator.clipboard.writeText(contactEmail);
      toast('联系邮箱已复制');
    } catch {
      toast('暂时无法复制，请选中邮箱地址手动复制');
    }
  }
  return (
    <section id="contact" className="information-contact" aria-labelledby="contact-title">
      <div>
        <span className="eyebrow">联系平台</span>
        <h2 id="contact-title">还有问题，我们一起解决。</h2>
        <p>平台使用、入驻合作、项目协作和意见反馈，都可以在这里联系。</p>
        <p className="information-contact-hint">
          请提供相关需求或项目编号、问题描述和发生时间，方便核查。密码、验证码和完整证件资料请勿通过普通咨询发送。
        </p>
      </div>
      <div className="information-contact-actions">
        <Button variant="secondary" onClick={openSupport}>
          <MessageSquare size={17} />
          在线咨询
        </Button>
        <div className="information-email">
          <a href={`mailto:${contactEmail}`}>
            <Mail size={17} />
            {contactEmail}
          </a>
          <IconButton label="复制联系邮箱" onClick={copyEmail}>
            <Copy size={16} />
          </IconButton>
        </div>
        <p>运营主体：{operator}</p>
      </div>
    </section>
  );
}

const journeys = [
  {
    role: 'client' as const,
    title: '我想拍一部片',
    description: '说明目标、预算和交付要求，寻找合适的团队。',
    icon: BriefcaseBusiness,
    steps: ['发布需求', '沟通合作', '项目与素材管理'],
    action: '发布拍片需求',
    path: '/publish',
  },
  {
    role: 'provider' as const,
    title: '我提供制作服务',
    description: '用真实作品展示能力，参与需求并交付项目。',
    icon: Film,
    steps: ['完善主页与案例', '交流合作意向', '管理项目与推广'],
    action: '成为服务商',
    path: registration('provider'),
  },
  {
    role: 'resource' as const,
    title: '我提供影视资源',
    description: '连接场地、人才、器材等资源与实际拍摄需求。',
    icon: Camera,
    steps: ['完善资源资料', '联系资源发布人', '自主安排合作'],
    action: '成为资源方',
    path: registration('resource'),
  },
];

function JourneyCards({ concise = false }: { concise?: boolean }) {
  return (
    <div className="information-journeys">
      {journeys.map((journey) => (
        <article key={journey.role} className="information-card">
          <journey.icon size={24} strokeWidth={1.6} />
          <h3>{journey.title}</h3>
          <p>{journey.description}</p>
          {!concise && (
            <ol>
              {journey.steps.map((step) => (
                <li key={step}>{step}</li>
              ))}
            </ol>
          )}
          <ButtonLink to={journey.path} variant="secondary" className="btn-sm">
            {journey.action}
            <ArrowRight size={15} />
          </ButtonLink>
        </article>
      ))}
    </div>
  );
}

function ProcessSteps({ steps }: { steps: { title: string; text: string }[] }) {
  return (
    <ol className="information-process">
      {steps.map((step, index) => (
        <li key={step.title}>
          <span className="information-step-number">{String(index + 1).padStart(2, '0')}</span>
          <h3>{step.title}</h3>
          <p>{step.text}</p>
        </li>
      ))}
    </ol>
  );
}

export function AboutPage() {
  return (
    <div className="container information-page">
      <Breadcrumb title="关于影爸爸" />
      <section className="information-about-hero">
        <div>
          <span className="eyebrow">关于影爸爸</span>
          <h1>
            让好创意，
            <br />
            遇见合拍的人。
          </h1>
          <p className="information-lead">
            影爸爸连接企业客户、专业影视团队与行业资源，帮助你把拍片需求变成清晰的合作，把专业能力变成看得见的作品。
          </p>
          <p className="muted">
            从需求发现与合作沟通，到团队任务、素材版本与视频推广，让创作拥有清晰的工作空间。
          </p>
          <div className="information-actions">
            <ButtonLink to="/publish">
              发布拍片需求
              <ArrowRight size={16} />
            </ButtonLink>
            <ButtonLink to="/providers" variant="secondary">
              寻找制作团队
            </ButtonLink>
          </div>
        </div>
        <figure>
          <Image src={imageAssets.studio} alt="摄影棚里的专业拍摄设备" />
          <figcaption>创作场景参考 · 让专业的人专注于创作</figcaption>
        </figure>
      </section>
      <section className="information-section" aria-labelledby="audience-title">
        <div className="information-section-heading">
          <span className="eyebrow">你可以从这里开始</span>
          <h2 id="audience-title">不同的专业，同一个合作现场。</h2>
          <p>选择适合你的合作方式，进入对应的工作流程。</p>
        </div>
        <JourneyCards />
      </section>
      <section className="information-section" aria-labelledby="process-title">
        <div className="information-section-heading">
          <span className="eyebrow">从需求到作品</span>
          <h2 id="process-title">把合作中的关键事项，放在一起。</h2>
        </div>
        <ProcessSteps
          steps={[
            {
              title: '把需求说明白',
              text: '整理目标、受众、预算、时间和参考效果，为团队判断合作提供依据。',
            },
            {
              title: '把约定确认好',
              text: '联系合适的团队，明确制作目标、工作范围与合作安排。',
            },
            {
              title: '把项目推进下去',
              text: '集中管理成员、任务、会议与素材，保留文件版本并跨项目复用。',
            },
            {
              title: '让作品继续发挥价值',
              text: '整理作品案例、渠道文案与推广计划，持续跟进发布效果。',
            },
          ]}
        />
      </section>
      <section className="information-section" aria-labelledby="principles-title">
        <div className="information-section-heading">
          <span className="eyebrow">认真对待每一次合作</span>
          <h2 id="principles-title">清晰的信息，是信任的起点。</h2>
        </div>
        <div className="information-journeys">
          {[
            {
              icon: FileText,
              title: '用真实内容展示专业',
              text: '团队介绍、服务产品、资源和案例按流程审核。主体认证单独展示，便于识别合作对象；实际能力、档期和权利范围仍需双方核对。',
            },
            {
              icon: Users,
              title: '用明确分工减少反复',
              text: '按组织和项目管理成员权限。任务、确认事项、会议纪要和交付版本集中记录，帮助参与者理解各自的下一步。',
            },
            {
              icon: ShieldCheck,
              title: '用完整记录支持合作',
              text: '自主决定项目资料的分享范围。合作方接受邀请后才能进入项目，人员变动时可及时调整访问权限。',
            },
          ].map((item) => (
            <article key={item.title} className="information-card">
              <item.icon size={24} strokeWidth={1.6} />
              <h3>{item.title}</h3>
              <p>{item.text}</p>
            </article>
          ))}
        </div>
      </section>
      <div className="information-related">
        <BookOpen size={20} />
        <div>
          <strong>第一次使用影爸爸？</strong>
          <p>查看入驻指引、合作流程和常见问题。</p>
        </div>
        <ButtonLink to="/help" variant="secondary">
          前往帮助中心
          <ArrowRight size={15} />
        </ButtonLink>
      </div>
      <ContactSection />
    </div>
  );
}

export function HelpPage() {
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const rawCategory = params.get('category') || '';
  const category = helpCategories.some((item) => item.id === rawCategory) ? rawCategory : '';
  const query = params.get('q') || '';
  const allQuestions = searchHelp(category, query);
  let anchor = '';
  try {
    anchor = decodeURIComponent(location.hash.slice(1));
  } catch {
    /* Ignore malformed links. */
  }
  const featured = [
    'digital-resources',
    'project-brief',
    'project-navigation',
    'publish-demand',
    'provider-onboarding',
    'password-reset',
  ];
  const compact =
    !category &&
    !query.trim() &&
    params.get('all') !== '1' &&
    !allQuestions.some((item) => item.id === anchor);
  const questions = compact
    ? featured.flatMap((id) => allQuestions.filter((item) => item.id === id))
    : allQuestions;
  const activeCategory = helpCategories.find((item) => item.id === category);
  const latestQuery = useRef(query);
  useEffect(() => {
    latestQuery.current = query;
  }, [query]);
  function filter(nextCategory: string, nextQuery?: string) {
    const text = nextQuery === undefined ? latestQuery.current : nextQuery;
    latestQuery.current = text;
    const next = new URLSearchParams();
    if (nextCategory) next.set('category', nextCategory);
    if (text) next.set('q', text);
    setParams(next, { replace: true });
  }
  useEffect(() => {
    const id = location.hash.slice(1);
    const element = document.getElementById(id);
    if (element instanceof HTMLDetailsElement) element.open = true;
  }, [location.hash, category, query]);
  return (
    <div className="container information-page help-page">
      <Breadcrumb title="帮助中心" />
      <header className="help-hero">
        <span className="eyebrow">影爸爸使用指南</span>
        <h1>你好，有什么可以帮到你？</h1>
        <p>从第一次注册，到项目协作、素材复用和视频推广，找到你需要的下一步。</p>
        <form
          role="search"
          className="help-search"
          onSubmit={(event) => {
            event.preventDefault();
            document.getElementById('help-results')?.scrollIntoView({ block: 'start' });
          }}
        >
          <Search size={21} aria-hidden="true" />
          <input
            type="search"
            aria-label="搜索帮助内容"
            placeholder="搜索：发布需求、项目素材、推广、找回密码…"
            value={query}
            onChange={(event) => filter(category, event.target.value)}
          />
          {query && (
            <IconButton label="清除帮助搜索" onClick={() => filter(category, '')}>
              <X size={17} />
            </IconButton>
          )}
          <Button type="submit" variant="secondary" className="help-submit">
            搜索
          </Button>
        </form>
        <div className="help-popular">
          <span>常见问题</span>
          {[
            ['发布需求', 'publish-demand'],
            ['如何入驻', 'provider-onboarding'],
            ['找回密码', 'password-reset'],
          ].map(([label, id]) => (
            <Link
              key={id}
              to={`/help?category=${id === 'publish-demand' ? 'demands' : 'start'}#${id}`}
            >
              {label}
            </Link>
          ))}
        </div>
      </header>
      {!category && !query.trim() ? (
        <section id="help-start" aria-labelledby="help-start-title" className="help-start">
          <h2 id="help-start-title">按你的角色，快速开始</h2>
          <JourneyCards concise />
        </section>
      ) : (
        <p className="help-guide-link">
          <Link to="/help#help-start">
            第一次使用？查看角色入门指引
            <ArrowRight size={14} />
          </Link>
        </p>
      )}
      <section className="help-library" aria-labelledby="help-library-title">
        <aside className="help-category-panel">
          <h2 id="help-library-title">使用指南</h2>
          <nav aria-label="帮助分类">
            <button
              type="button"
              className={!category ? 'active' : ''}
              aria-pressed={!category}
              onClick={() => filter('')}
            >
              <BookOpen size={17} />
              全部分类
            </button>
            {helpCategories.map((item) => (
              <button
                key={item.id}
                type="button"
                className={category === item.id ? 'active' : ''}
                aria-pressed={category === item.id}
                onClick={() => filter(item.id)}
              >
                {item.label}
              </button>
            ))}
          </nav>
          <p>需要继续项目协作或整理素材？登录后进入对应组织的工作台。</p>
          <a href="#contact" className="link">
            联系平台
            <ArrowRight size={14} />
          </a>
        </aside>
        <div id="help-results" className="help-questions">
          <div className="help-results-heading">
            <div>
              <h2>{activeCategory?.label || '常见问题'}</h2>
              <p>{activeCategory?.description || '选择一个问题，展开查看操作说明。'}</p>
            </div>
            <span role="status" aria-live="polite">
              {query.trim() ? '找到 ' : ''}
              {questions.length} 个问题
            </span>
          </div>
          {questions.length ? (
            questions.map((question) => (
              <details
                key={question.id}
                id={question.id}
                className="help-question"
                open={query.trim() ? true : undefined}
              >
                <summary>
                  {question.title}
                  <ChevronDown size={18} aria-hidden="true" />
                </summary>
                <div className="help-answer">
                  <span className="help-answer-category">
                    {helpCategories.find((item) => item.id === question.category)?.label}
                  </span>
                  {question.paragraphs.map((paragraph) => (
                    <p key={paragraph}>{paragraph}</p>
                  ))}
                  <div className="help-answer-actions">
                    {question.action && (
                      <ButtonLink
                        to={
                          question.action.workspace
                            ? siteURL('work', question.action.path)
                            : question.action.path
                        }
                        variant="secondary"
                        className="btn-sm"
                      >
                        {question.action.label}
                        {question.action.workspace ? (
                          <ArrowUpRight size={14} />
                        ) : (
                          <ArrowRight size={14} />
                        )}
                      </ButtonLink>
                    )}
                    <Link
                      className="link small"
                      to={`/help?category=${question.category}#${question.id}`}
                    >
                      此问题的链接
                    </Link>
                  </div>
                </div>
              </details>
            ))
          ) : (
            <div className="help-no-results">
              <Search size={27} />
              <h3>没有找到对应的问题</h3>
              <p>可以换一个关键词，或查看全部分类；仍未解决时，请联系平台。</p>
              <div className="information-actions">
                {category && query.trim() && (
                  <Button variant="secondary" onClick={() => filter('')}>
                    搜索全部分类
                  </Button>
                )}
                <Button variant="secondary" onClick={() => filter('', '')}>
                  重置搜索与分类
                </Button>
              </div>
            </div>
          )}
          {compact && (
            <div className="help-more">
              <Button
                variant="secondary"
                onClick={() => setParams({ all: '1' }, { replace: true })}
              >
                查看全部 {allQuestions.length} 个问题
              </Button>
            </div>
          )}
        </div>
      </section>
      <ContactSection />
      <div className="information-policy-links">
        <Link to="/terms">服务条款</Link>
        <Link to="/privacy">隐私政策</Link>
        <Link to="/about">了解影爸爸</Link>
      </div>
    </div>
  );
}

export function FeaturePage({ kind }: { kind: 'ai' | 'promotion' }) {
  const ai = kind === 'ai';
  const title = ai ? 'AI 创作' : '视频推广';
  return (
    <div className="container information-page">
      <Breadcrumb title={title} />
      <section className="information-about-hero information-feature-hero">
        <div>
          <span className="eyebrow">{ai ? 'AI 创作助手' : '作品交付之后'}</span>
          <h1>
            {ai ? (
              <>
                把想法，变成
                <br />
                可讨论的创作方案。
              </>
            ) : (
              <>
                让每一部好作品，
                <br />
                有清晰的传播计划。
              </>
            )}
          </h1>
          <p className="information-lead">
            {ai
              ? '整理需求、起草脚本、规划文字分镜和营销文案。把更多时间留给创意判断，让每一次修改都有版本可循。'
              : '按渠道准备内容，安排发布时间，跟进发布状态。把散落的文案、任务和作品链接，整理成团队可协作的推广记录。'}
          </p>
          <div className="information-actions">
            <ButtonLink to={siteURL('work', ai ? '/ai' : '/promotion')}>
              {ai ? '进入创作工作台' : '创建推广计划'}
              <ArrowUpRight size={16} />
            </ButtonLink>
            <Link
              to={`/help?category=creation#${ai ? 'ai-capabilities' : 'promotion-publish'}`}
              className="link"
            >
              查看使用说明
              <ArrowRight size={15} />
            </Link>
          </div>
        </div>
        <figure>
          <Image
            src={ai ? imageAssets.hero : imageAssets.event}
            alt={ai ? '影视创作的分镜策划与参考画面' : '专业团队拍摄活动现场'}
          />
          <figcaption>{ai ? '创作灵感参考' : '创作场景参考'}</figcaption>
        </figure>
      </section>
      <section className="information-section" aria-labelledby="feature-tools-title">
        <div className="information-section-heading">
          <span className="eyebrow">{ai ? '先把创作说清楚' : '从准备到发布'}</span>
          <h2 id="feature-tools-title">
            {ai ? '为不同的创作阶段，准备一份好草稿。' : '每个渠道，都有可跟进的任务。'}
          </h2>
        </div>
        <div className={`information-tools ${ai ? 'four-columns' : ''}`}>
          {(ai
            ? [
                {
                  icon: FileText,
                  title: '创作需求',
                  text: '整理品牌背景、受众、传播目标、必备信息和交付要求。',
                },
                {
                  icon: Film,
                  title: '视频脚本',
                  text: '起草故事结构、段落内容、旁白与节奏，方便团队讨论。',
                },
                {
                  icon: Camera,
                  title: '文字分镜',
                  text: '把画面、镜头、声音和时长组织成可继续细化的镜头计划。',
                },
                {
                  icon: Megaphone,
                  title: '营销文案',
                  text: '围绕作品用途起草标题与正文，再结合品牌口径编辑确认。',
                },
              ]
            : [
                {
                  icon: FileText,
                  title: '内容准备',
                  text: '为视频号、抖音、小红书、哔哩哔哩等渠道整理标题、正文和发布要求。',
                },
                {
                  icon: Megaphone,
                  title: '计划发布',
                  text: '确认内容准备就绪，设置计划时间，让团队看见接下来的安排。',
                },
                {
                  icon: CheckCircle2,
                  title: '发布与效果',
                  text: '保存真实作品链接，按统计时间记录渠道效果和来源，留给团队复盘。',
                },
              ]
          ).map((item) => (
            <article className="information-card" key={item.title}>
              <item.icon size={24} strokeWidth={1.6} />
              <h3>{item.title}</h3>
              <p>{item.text}</p>
            </article>
          ))}
        </div>
      </section>
      <section className="information-section" aria-labelledby="feature-process-title">
        <div className="information-section-heading">
          <h2 id="feature-process-title">
            {ai ? '三步开始，把草稿变成团队可以使用的方案。' : '四步整理，让发布有准备、有记录。'}
          </h2>
        </div>
        <ProcessSteps
          steps={
            ai
              ? [
                  {
                    title: '写下具体背景',
                    text: '填写创作标题、类型与想法，说明品牌、受众、用途、时长和必须保留的信息。',
                  },
                  {
                    title: '检查并完善内容',
                    text: '查看生成状态，核对事实与表达，补充拍摄条件、素材权利和品牌要求。',
                  },
                  {
                    title: '保存与分享版本',
                    text: '编辑后保存新版本；确认的版本可以锁定或导出文本，再交给团队继续制作。',
                  },
                ]
              : [
                  {
                    title: '建立渠道任务',
                    text: '为每个发布渠道创建任务，准备标题与文案，填写计划时间。',
                  },
                  {
                    title: '确认准备就绪',
                    text: '核对成片、封面、文案和授权，把任务移入计划发布。',
                  },
                  {
                    title: '在目标渠道发布',
                    text: '由你在对应平台完成实际发布，并按该平台规则检查结果。',
                  },
                  {
                    title: '记录链接与效果',
                    text: '保存作品链接，记录渠道后台的播放、互动等数据，跟进每次发布的效果。',
                  },
                ]
          }
        />
      </section>
      <section className="information-scope" aria-labelledby="scope-title">
        <span className="information-scope-icon">
          {ai ? <Sparkles size={24} /> : <FolderOpen size={24} />}
        </span>
        <div>
          <h2 id="scope-title">{ai ? '使用前，了解当前创作范围' : '推广工作台的服务范围'}</h2>
          <p>
            {ai
              ? '当前 AI 工作台提供文字内容与版本管理。图片绘制、视频生成、剪辑和切片需使用相应制作工具；生成服务不可用时，可以先建立、编辑结构化草稿。正式使用前请核对事实、授权和制作可行性。'
              : '在这里整理项目素材、封面、文案和发布计划，前往对应平台完成发布，再保存作品链接、记录渠道后台数据。每次统计都会保留，方便持续比较推广效果。'}
          </p>
          <Link
            to={`/help?category=creation#${ai ? 'ai-retry' : 'promotion-publish'}`}
            className="link"
          >
            {ai ? '遇到生成问题，查看处理方法' : '了解发布状态如何更新'}
            <ArrowRight size={15} />
          </Link>
        </div>
      </section>
      <div className="information-related">
        <BookOpen size={20} />
        <div>
          <strong>
            {ai
              ? '方案确认好，就可以开始寻找合作团队。'
              : '还在筹备拍摄？先把需求和交付目标说明白。'}
          </strong>
          <p>
            {ai
              ? '把创作稿作为沟通参考，具体费用与交付范围仍需双方确认。'
              : '从一开始考虑传播用途，让制作和发布衔接起来。'}
          </p>
        </div>
        <ButtonLink to={ai ? '/providers' : '/publish'} variant="secondary">
          {ai ? '寻找制作团队' : '发布拍片需求'}
          <ArrowRight size={15} />
        </ButtonLink>
      </div>
      <ContactSection />
    </div>
  );
}
